/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import isString from 'lodash.isstring';
import {error} from '../logger/logger';

/* eslint-disable ion-eslint-rules/safe-json-parse */

/**
 * Provides a utility which determines whether a value is a string that looks like
 * a JSON object or array, based on its leading character, without attempting to
 * parse it. Useful for guarding a `safeJSONParse` call against fields that are
 * usually plain text and only sometimes JSON-encoded, avoiding a logged parse
 * error for the common plain-text case.
 *
 * @param value The value to check.
 * @returns `true` if `value` is a string starting with `{` or `[`, otherwise `false`.
 */
export const isJSONDescription = (value: unknown): value is string => {
  if (typeof value !== 'string') {
    return false;
  }
  const trimmed = value.trim();
  return trimmed.startsWith('{') || trimmed.startsWith('[');
};

/**
 * Provides a safe-guard utility which ensures calls to `JSON.parse` are invoked
 * within a try / catch block, returning the parsed result if valid, otherwise
 * returns fallback value.
 *
 * Returns `fallback` if provided and parsing fails or the input is empty.
 * Returns the original `value` when no fallback is provided.
 * Logs a detailed error message when JSON parsing throws.
 *
 * @param value    The JSON string to be parsed.
 * @param fallback Optional value to return if parsing fails or input is invalid.
 */
export const safeJSONParse = <T = unknown>(value: string, fallback?: T): T | unknown => {
  if (isString(value) && value.trim().length) {
    try {
      return JSON.parse(value);
    } catch (err) {
      error(`[safeJSONParse] Error Parsing JSON String: ${value}. Error: ${err?.toString()}`);
    }
  }
  return fallback ?? value;
};

/**
 * Provides a safe-guard utility which ensures calls to `JSON.stringify` are invoked
 * within a try / catch block, returning the stringified result if valid, otherwise
 * returns undefined.
 *
 * ```typescript
 *
 *  import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
 *
 *   const parsed = safeJSONParse({name: "Asset 01, Asset 02"});
 *   // "{\"name\":\"Asset 01, Asset 02\"}"
 *
 * ```
 */
export const safeJSONStringify = (value: any): string | undefined => {
  if (value) {
    try {
      return JSON.stringify(value);
    } catch (err) {
      error(`[safeJSONStringify] Error JSON Stringify: ${value}. Error: ${err?.toString()}`);
    }
  }
};
